import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";

// The gateway holds ~1,200 accounts; cap the rendered drill-in list so the DOM
// stays light. The count tile always shows the true total.
const MAX_VISIBLE = 50;

export type DirectoryItem = {
  key: string;
  name: string;
  isActive: boolean;
  lastSyncedAt: string;
};

export type SyncTile = {
  label: string;
  count: number;
  /** When present (and count > 0) the tile is expandable and drills into a list. */
  items?: DirectoryItem[];
};

/** The most recent `lastSyncedAt` across a set of items, formatted, or null. */
export function lastSyncedLabel(items: DirectoryItem[]): string | null {
  if (items.length === 0) return null;
  const latest = items.reduce((max, i) => Math.max(max, new Date(i.lastSyncedAt).getTime()), 0);
  return latest > 0 ? new Date(latest).toLocaleString() : null;
}

function ItemList({ items }: { items: DirectoryItem[] }) {
  if (items.length === 0) {
    return <p className="px-1 py-2 text-sm text-muted-foreground">Nothing synced yet.</p>;
  }
  const visible = items.slice(0, MAX_VISIBLE);
  const hidden = items.length - visible.length;
  return (
    <div className="mt-2">
      <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border/50">
        {visible.map((i) => (
          <li key={i.key} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
            <span className="truncate">{i.name}</span>
            <StatusBadge status={i.isActive ? "completed" : "disabled"} className="shrink-0">
              {i.isActive ? "active" : "inactive"}
            </StatusBadge>
          </li>
        ))}
      </ul>
      {hidden > 0 ? (
        <p className="mt-1.5 text-xs text-muted-foreground">+ {hidden} more not shown.</p>
      ) : null}
    </div>
  );
}

function StatTile({
  tile,
  expanded,
  onToggle,
}: {
  tile: SyncTile;
  expanded: boolean;
  onToggle: () => void;
}) {
  const expandable = tile.items !== undefined && tile.count > 0;

  if (!expandable) {
    return (
      <div className="flex flex-1 items-center justify-between rounded-lg border border-border/50 px-4 py-3">
        <div>
          <div className="text-2xl font-semibold tabular-nums">{tile.count}</div>
          <div className="text-xs text-muted-foreground">{tile.label}</div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex flex-1 items-center justify-between rounded-lg border border-border/50 px-4 py-3 text-left transition-colors hover:bg-muted/50"
    >
      <div>
        <div className="text-2xl font-semibold tabular-nums">{tile.count}</div>
        <div className="text-xs text-muted-foreground">{tile.label}</div>
      </div>
      {expanded ? (
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      ) : (
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      )}
    </button>
  );
}

/**
 * The single, consistent card used for EVERY synchronization object (Solutions,
 * Accounts, Resources, Time Entry). One anatomy — icon + title + last-synced,
 * a per-card Refresh, a uniform row of stat tiles (expandable when they carry an
 * item list), an optional primary `action` (e.g. "Import N"), and an optional
 * `note` (a short caveat under the tiles) — so the objects read as a set.
 */
export function SyncObjectCard({
  title,
  icon,
  syncedLabel,
  tiles,
  onRefresh,
  refreshing,
  refreshDisabled = false,
  refreshTitle,
  action,
  note,
}: {
  title: string;
  icon: React.ReactNode;
  syncedLabel: string | null;
  tiles: SyncTile[];
  onRefresh: () => void;
  refreshing: boolean;
  refreshDisabled?: boolean;
  /** Hover text on the Refresh button — e.g. why it is disabled for this role. */
  refreshTitle?: string;
  action?: React.ReactNode;
  note?: string;
}) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const openTile = openIdx !== null ? tiles[openIdx] : undefined;

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center gap-3 pb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
          {icon}
        </div>
        <div className="flex-1">
          <CardTitle className="text-sm">{title}</CardTitle>
          {syncedLabel ? (
            <p className="text-xs text-muted-foreground">last synced {syncedLabel}</p>
          ) : (
            <p className="text-xs text-muted-foreground">not synced yet</p>
          )}
        </div>
        {action}
        {/* Title on a wrapper: a disabled Button ignores pointer events, so it can't show one. */}
        <span className="inline-flex" title={refreshTitle}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={refreshDisabled || refreshing}
            aria-label={`Refresh ${title}`}
          >
            <RefreshCw className={refreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          </Button>
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-3">
          {tiles.map((tile, idx) => (
            <StatTile
              key={tile.label}
              tile={tile}
              expanded={openIdx === idx}
              onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
            />
          ))}
        </div>
        {openTile?.items ? <ItemList items={openTile.items} /> : null}
        {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
      </CardContent>
    </Card>
  );
}
